import { ASSET_TYPES } from "../../enums/assetTypes.enum";
import { transformManufacturerName } from "../../shared/utils/utils";
import { Address } from "../shared/address";
import { Reference } from "../shared/refs";

export interface Core {
  id?: string;
  serialNumber?: string;
  assigned: boolean;
}

export interface TenantSettingsDTO {
  id?: string;
  notificationTimeFE?: number;
  notificationTimeGuard?: number;
}

export interface TenantDTO extends TenantSettingsDTO {
  id?: string;
  label: string;
  address?: Address;
  users?: UserCompact[];
  cores?: Core[];
  phoneNumber?: string;
  emailAddress?: string;
  notes?: string;
  noCoreLimit?: number | null;
  numberOfCustomers?: number;
  numberOfLocations?: number;
  numberOfBuildings?: number;
  numberOfGateSystems?: number;
  // On POST this is string[] and on GET it is Reference[]:
  manufacturers?: Reference[] | string[] | null;
  models?: Reference[] | string[] | null;
}

export interface UserCompact {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
}

export class Tenant {
  id?: string;
  label: string;
  address?: Address;
  users?: UserCompact[];
  cores?: Core[];
  phoneNumber?: string;
  email?: string;
  notes?: string;
  assetType: string;
  noCoreLimit?: number | null;
  numberOfCustomers?: number;
  numberOfLocations?: number;
  numberOfBuildings?: number;
  numberOfGateSystems?: number;
  notificationTimeFE?: number;
  notificationTimeGuard?: number;
  manufacturers?: Reference[];
  models?: Reference[];

  constructor(tenant: TenantDTO) {
    if (tenant.noCoreLimit !== undefined) {
      this.noCoreLimit = Number(tenant.noCoreLimit);
    } else {
      this.noCoreLimit = null;
    }
    this.id = tenant.id;
    this.label = tenant.label;
    this.address = tenant.address;
    this.users = tenant.users;
    this.phoneNumber = tenant.phoneNumber;
    this.email = tenant.emailAddress;
    this.notes = tenant.notes;
    this.numberOfCustomers = tenant.numberOfCustomers;
    this.numberOfLocations = tenant.numberOfLocations;
    this.numberOfBuildings = tenant.numberOfBuildings;
    this.numberOfGateSystems = tenant.numberOfGateSystems;
    this.notificationTimeFE = tenant.notificationTimeFE;
    this.notificationTimeGuard = tenant.notificationTimeGuard;
    this.cores = tenant.cores;
    this.manufacturers = (tenant.manufacturers as Reference[])
      ? (tenant.manufacturers as Reference[]).map((manufacturer) => {
          manufacturer.label = transformManufacturerName(manufacturer.label);
          return manufacturer;
        })
      : [];
    this.models = (tenant.models as Reference[]) ? (tenant.models as Reference[]) : [];
    this.assetType = ASSET_TYPES.TENANT;
  }

  public set properties(tenant: TenantDTO) {
    this.label = tenant.label;
    this.email = tenant.emailAddress;
    this.phoneNumber = tenant.phoneNumber;
    this.notes = tenant.notes;
    this.address = tenant.address;
    this.noCoreLimit = tenant.noCoreLimit;
  }

  public readonly directParentId = null;

  public get type() {
    return this.assetType;
  }

  public get values() {
    const tenant: TenantDTO = {
      label: this.label,
    };
    if (this.email) {
      tenant.emailAddress = this.email;
    }
    if (this.phoneNumber) {
      tenant.phoneNumber = this.phoneNumber;
    }
    if (this.address) {
      if (
        this.address.street &&
        this.address.number &&
        this.address.city &&
        this.address.country &&
        this.address.zipCode
      ) {
        tenant.address = this.address;
        if (!this.address.street) {
          delete tenant.address.street;
        }
        if (!this.address.number) {
          delete tenant.address.number;
        }
        if (!this.address.city) {
          delete tenant.address.city;
        }
        if (!this.address.country) {
          delete tenant.address.country;
        }
        if (!this.address.zipCode) {
          delete tenant.address.zipCode;
        }
      }
    }
    if (this.manufacturers) {
      tenant.manufacturers = this.manufacturers.reduce((acc: string[], manufacturer) => {
        if (manufacturer.id) acc.push(manufacturer.id);
        return acc;
      }, []);
    } else {
      tenant.manufacturers = [];
    }
    if (this.models) {
      tenant.models = this.models.reduce((acc: string[], model) => {
        if (model.id) acc.push(model.id);
        return acc;
      }, []);
    } else {
      tenant.models = [];
    }
    if (this.notes) {
      tenant.notes = this.notes;
    }
    if (this.noCoreLimit !== null) {
      tenant.noCoreLimit = this.noCoreLimit;
    }

    return tenant;
  }

  public get settings(): TenantSettingsDTO {
    return {
      notificationTimeFE: this.notificationTimeFE,
      notificationTimeGuard: this.notificationTimeGuard,
    };
  }
}
